import { loadConnectAndInitialize } from "@stripe/connect-js"
import PropTypes from "prop-types"
import React, { useCallback, useEffect, useState } from "react"
import { useQuery } from "react-query"
import BankTab from "src/main/Settings/StripeConnect/BankTab"
import PayoutsTab from "src/main/Settings/StripeConnect/PayoutsTab"

import Tabs from "src/components/Tabs"

import {
  createAccountSettingsSession,
  queryOnboardingCompletion,
} from "src/api/StripeConnect"

import { updateUrlQueryParams } from "src/utils/url/parsing/marina"

const StripeConnectSettingsContainer = ({
  stripeManagedAccountId,
  smaCountries,
  onboardingRequiredFieldsComplete,
}) => {
  const urlParams = new URLSearchParams(window.location.search)
  const activeTab = urlParams.get("active_tab")
  const [currentTab, setCurrentTab] = useState(0)
  const [stripeConnectInstance, setStripeConnectInstance] = useState(null)
  const [currentAccountId, setCurrentAccountId] = useState(
    stripeManagedAccountId
  )
  const [onboardingComplete, setOnboardingComplete] = useState(
    onboardingRequiredFieldsComplete
  )
  const [checkOnboardingComplete, setCheckOnboardingComplete] = useState(false)

  const { isLoading: isSessionLoading, error: sessionError } = useQuery(
    ["stripeAccountSettings", currentAccountId],
    () => createAccountSettingsSession({ stripeAccountId: currentAccountId }),
    {
      enabled: !!currentAccountId,
      onSuccess: async (response) => {
        const stripeConnect = await loadConnectAndInitialize({
          publishableKey: DockwaConfig.STRIPE_PUBLISHABLE_KEY,
          fetchClientSecret: () => Promise.resolve(response.client_secret),
          appearance: {
            variables: {
              colorPrimary: "#1e4acb",
            },
          },
        })

        setStripeConnectInstance(stripeConnect)
      },
    }
  )

  const {
    isLoading: isCheckingOnboardingComplete,
    error: checkingOnboardingError,
  } = useQuery(
    ["stripeAccountSettings", "onboardingCompletionCheck", currentAccountId],
    () => queryOnboardingCompletion({ stripeAccountId: currentAccountId }),
    {
      enabled: checkOnboardingComplete,
      onSuccess: (data) => {
        setCheckOnboardingComplete(false)
        setOnboardingComplete(data.onboardingComplete)
      },
    }
  )

  const tabName = useCallback((tab) => {
    switch (tab) {
      case 0:
        return "bank"
      case 1:
        return "payouts"
      default:
        return "bank"
    }
  }, [])

  const handleTabChange = (tab) => {
    setCurrentTab(tab)
    updateUrlQueryParams({ active_tab: tabName(tab) })
  }

  useEffect(() => {
    if (activeTab === "payouts") {
      setCurrentTab(1)
      updateUrlQueryParams({ active_tab: tabName(1) })
    } else {
      setCurrentTab(0)
      updateUrlQueryParams({
        active_tab: tabName(0),
      })
    }
  }, [activeTab, tabName])

  const handleAccountCreated = (accountId) => {
    setCurrentAccountId(accountId)
  }

  const handleOnboardingSuccess = () => {
    console.log("onboarding success")
    setCheckOnboardingComplete(true)
  }

  return (
    <div>
      <Tabs selectedIndex={currentTab} onChange={handleTabChange}>
        <Tabs.TabList>
          <Tabs.Tab title="Bank" />
          <Tabs.Tab title="Payouts" />
        </Tabs.TabList>
        <Tabs.Panels>
          <Tabs.Panel>
            <BankTab
              handleAccountCreated={handleAccountCreated}
              handleOnboardingSuccess={handleOnboardingSuccess}
              isSessionLoading={
                isSessionLoading || isCheckingOnboardingComplete
              }
              onboardingRequiredFieldsComplete={onboardingComplete}
              sessionError={sessionError || checkingOnboardingError}
              smaCountries={smaCountries}
              stripeConnectInstance={stripeConnectInstance}
              stripeManagedAccountId={currentAccountId}
            />
          </Tabs.Panel>
          <Tabs.Panel>
            <PayoutsTab
              isSessionLoading={
                isSessionLoading || isCheckingOnboardingComplete
              }
              stripeConnectInstance={stripeConnectInstance}
              onboardingRequiredFieldsComplete={onboardingComplete}
              sessionError={sessionError || checkingOnboardingError}
            />
          </Tabs.Panel>
        </Tabs.Panels>
      </Tabs>
    </div>
  )
}

StripeConnectSettingsContainer.propTypes = {
  stripeManagedAccountId: PropTypes.string,
  smaCountries: PropTypes.arrayOf(
    PropTypes.shape({
      countryCode: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  onboardingRequiredFieldsComplete: PropTypes.bool.isRequired,
}

export default StripeConnectSettingsContainer
